import { WebsiteBlockTextAndMediaType } from '../api/Enums';
import { WebsiteBlockDisplayItem } from '../api/WebsiteBlockDisplayItem';
import { WebsiteBlockTextAndMediaDisplayItem } from '../api/WebsiteBlockTextAndMediaDisplayItem';

declare module '../api/WebsiteBlockTextAndMediaDisplayItem' {
    namespace WebsiteBlockTextAndMediaDisplayItem {
        let IsCallToActionBlock: typeof isCallToActionBlock;
        let IsGalleryBlock: typeof isGalleryBlock;
        let IsHeroBlock: typeof isHeroBlock;
        let IsHTMLBlock: typeof isHTMLBlock;
        let IsPhotoGalleryBlock: typeof isPhotoGalleryBlock;
        let IsTestimonialBlock: typeof isTestimonialBlock;
        let IsTextBlock: typeof isTextBlock;
        let HasImages: typeof hasImages;

        type CallToActionType = TextAndMediaType<typeof callToActionBlockTypes>;
        type GalleryType = TextAndMediaType<typeof galleryBlockTypes>;
        type HeroType = TextAndMediaType<typeof heroBlockTypes>;
        type HTMLType = TextAndMediaType<typeof htmlBlockTypes>;
        type PhotoGalleryType = TextAndMediaType<typeof photoGalleryBlockTypes>;
        type TestimonialType = TextAndMediaType<typeof testimonialBlockTypes>;
        type TextType = TextAndMediaType<typeof textBlockTypes>;

        type CallToActionBlock = TextAndMediaBlockType<typeof callToActionBlockTypes>;
        type GalleryBlock = TextAndMediaBlockType<typeof galleryBlockTypes>;
        type HeroBlock = TextAndMediaBlockType<typeof heroBlockTypes>;
        type HTMLBlock = TextAndMediaBlockType<typeof htmlBlockTypes>;
        type PhotoGalleryBlock = TextAndMediaBlockType<typeof photoGalleryBlockTypes>;
        type TestimonialBlock = TextAndMediaBlockType<typeof testimonialBlockTypes>;
        type TextBlock = TextAndMediaBlockType<typeof textBlockTypes>;
    }
}

const callToActionBlockTypes = [
    WebsiteBlockTextAndMediaType.CallToActionDefault,
    WebsiteBlockTextAndMediaType.CallToActionDescription,
    WebsiteBlockTextAndMediaType.CallToActionAccentBackground,
    WebsiteBlockTextAndMediaType.CallToActionAccentBackgroundDescription
] as const;

const galleryBlockTypes = [
    WebsiteBlockTextAndMediaType.GalleryCircleAccentColumn,
    WebsiteBlockTextAndMediaType.GalleryCircleCardColumn,
    WebsiteBlockTextAndMediaType.GalleryCircleColumn,
    WebsiteBlockTextAndMediaType.GallerySquareAccentColumn,
    WebsiteBlockTextAndMediaType.GallerySquareCardColumn,
    WebsiteBlockTextAndMediaType.GallerySquareColumn
] as const;

const heroBlockTypes = [
    WebsiteBlockTextAndMediaType.HeroNoImage,
    WebsiteBlockTextAndMediaType.HeroBackgroundImage,
    WebsiteBlockTextAndMediaType.HeroSquareSideImage,
    WebsiteBlockTextAndMediaType.HeroClippedSideImage,
    WebsiteBlockTextAndMediaType.MediaLeftHeroClippedImage,
    WebsiteBlockTextAndMediaType.MediaLeftHeroSquareImage
] as const;

const htmlBlockTypes = [
    WebsiteBlockTextAndMediaType.HTML
] as const;

const photoGalleryBlockTypes = [
    WebsiteBlockTextAndMediaType.PhotoGalleryCarousel,
    WebsiteBlockTextAndMediaType.PhotoGalleryCarouselOverlay,
    WebsiteBlockTextAndMediaType.PhotoGalleryGrid
] as const;

const testimonialBlockTypes = [
    WebsiteBlockTextAndMediaType.Testimonial,
    WebsiteBlockTextAndMediaType.AccentBackgroundTestimonial,
    WebsiteBlockTextAndMediaType.MediaRightTestimonial,
    WebsiteBlockTextAndMediaType.MediaRightAccentBackgroundTestimonial
] as const;

const textBlockTypes = [
    WebsiteBlockTextAndMediaType.Default,
    WebsiteBlockTextAndMediaType.TwoColumn,
    WebsiteBlockTextAndMediaType.AccentBackgroundDefault,
    WebsiteBlockTextAndMediaType.MediaLeftShadow,
    WebsiteBlockTextAndMediaType.MediaRightShadow,
    WebsiteBlockTextAndMediaType.MediaLeft,
    WebsiteBlockTextAndMediaType.MediaRight,
    WebsiteBlockTextAndMediaType.MediaLeftClipped,
    WebsiteBlockTextAndMediaType.MediaRightClipped,
    WebsiteBlockTextAndMediaType.MediaLeftTwoColumn,
    WebsiteBlockTextAndMediaType.MediaRightTwoColumn
] as const;

WebsiteBlockTextAndMediaDisplayItem.IsCallToActionBlock = isCallToActionBlock;
WebsiteBlockTextAndMediaDisplayItem.IsGalleryBlock = isGalleryBlock;
WebsiteBlockTextAndMediaDisplayItem.IsHeroBlock = isHeroBlock;
WebsiteBlockTextAndMediaDisplayItem.IsHTMLBlock = isHTMLBlock;
WebsiteBlockTextAndMediaDisplayItem.IsPhotoGalleryBlock = isPhotoGalleryBlock;
WebsiteBlockTextAndMediaDisplayItem.IsTestimonialBlock = isTestimonialBlock;
WebsiteBlockTextAndMediaDisplayItem.IsTextBlock = isTextBlock;
WebsiteBlockTextAndMediaDisplayItem.HasImages = hasImages;

function isCallToActionBlock(item: WebsiteBlockTextAndMediaDisplayItem): item is WebsiteBlockTextAndMediaDisplayItem.CallToActionBlock {
    return (callToActionBlockTypes as readonly WebsiteBlockTextAndMediaType[]).includes(item.TextAndMediaType);
}

function isGalleryBlock(item: WebsiteBlockTextAndMediaDisplayItem): item is WebsiteBlockTextAndMediaDisplayItem.GalleryBlock {
    return (galleryBlockTypes as readonly WebsiteBlockTextAndMediaType[]).includes(item.TextAndMediaType);
}

function isHeroBlock(item: WebsiteBlockTextAndMediaDisplayItem): item is WebsiteBlockTextAndMediaDisplayItem.HeroBlock {
    return (heroBlockTypes as readonly WebsiteBlockTextAndMediaType[]).includes(item.TextAndMediaType);
}

function isHTMLBlock(item: WebsiteBlockTextAndMediaDisplayItem): item is WebsiteBlockTextAndMediaDisplayItem.HeroBlock {
    return (htmlBlockTypes as readonly WebsiteBlockTextAndMediaType[]).includes(item.TextAndMediaType);
}

function isPhotoGalleryBlock(item: WebsiteBlockTextAndMediaDisplayItem): item is WebsiteBlockTextAndMediaDisplayItem.PhotoGalleryBlock {
    return (photoGalleryBlockTypes as readonly WebsiteBlockTextAndMediaType[]).includes(item.TextAndMediaType);
}

function isTestimonialBlock(item: WebsiteBlockTextAndMediaDisplayItem): item is WebsiteBlockTextAndMediaDisplayItem.TestimonialBlock {
    return (testimonialBlockTypes as readonly WebsiteBlockTextAndMediaType[]).includes(item.TextAndMediaType);
}

function isTextBlock(item: WebsiteBlockTextAndMediaDisplayItem): item is WebsiteBlockTextAndMediaDisplayItem.TextBlock {
    return (textBlockTypes as readonly WebsiteBlockTextAndMediaType[]).includes(item.TextAndMediaType);
}

function hasImages(item: WebsiteBlockDisplayItem): item is WebsiteBlockDisplayItem.WithImages {
    return WebsiteBlockDisplayItem.IsTextAndMedia(item)
           && (WebsiteBlockTextAndMediaDisplayItem.IsGalleryBlock(item)
              || WebsiteBlockTextAndMediaDisplayItem.IsHeroBlock(item)
                 && item.TextAndMediaType !== WebsiteBlockTextAndMediaType.HeroNoImage
              || WebsiteBlockTextAndMediaDisplayItem.IsPhotoGalleryBlock(item)
              || WebsiteBlockTextAndMediaDisplayItem.IsTestimonialBlock(item)
              || WebsiteBlockTextAndMediaDisplayItem.IsTextBlock(item)
                 && [WebsiteBlockTextAndMediaType.MediaLeftShadow, WebsiteBlockTextAndMediaType.MediaRightShadow,
                     WebsiteBlockTextAndMediaType.MediaLeft, WebsiteBlockTextAndMediaType.MediaRight,
                     WebsiteBlockTextAndMediaType.MediaLeftClipped, WebsiteBlockTextAndMediaType.MediaRightClipped].includes(item.TextAndMediaType));
}

type TextAndMediaType<T extends readonly any[]> = T[number];
type TextAndMediaBlockType<T extends readonly any[]> = WebsiteBlockTextAndMediaDisplayItem & { TextAndMediaType: T[number] };
